// Learn & Earn test history (LB-366, drawings signed off 23 Sep:
// https://claude.ai/artifact/N6oRvRthix3ZAj8DeL1xZd).
//
// Every test a child finishes (or stops part-way through) is kept, per game, with the
// questions, the child's own answers and what it paid, so the child ("My test history") and
// the parent ("Test history") can open any recent test and see exactly what happened.
//
// Why a slice of its own rather than more fields on the Coach log (adaptive[game].log):
//   - the log is the engine's, and the engine file is not edited (Alan, 14 Sep). It keeps
//     12 entries of {day, score, total}; adding the questions to it would make every engine
//     read carry them, and a change here could move a level.
//   - households with Learning Coach OFF must have a history too (Alan, 23 Sep), and this
//     slice does not care who chose the words.
// So: learningContent[childId].testHistory = { [game]: [record, ...] }, oldest first, and
// the log is untouched. Nothing here is read by the engine.
//
// NOT hardcoded (Alan, 23 Sep: "if we add more game types, then they will be shown here
// too"): the per-game limit is one constant, the games come from HISTORY_GAMES, and any
// game key found in a child's history that the registry does not know yet still shows,
// under its own id, rather than being dropped.

export const HISTORY_PER_GAME = 5;

// The order the filter chips appear in. A new Learn & Earn game adds one line here (its
// label is a CMS key, history.game.<id>, with this fallback).
export const HISTORY_GAMES = [
  { id: "spelling", name: "Spelling", icon: "ic-spelling" },
  { id: "vocab", name: "Vocabulary", icon: "ic-vocab" },
  { id: "ttstreaks", name: "Times tables", icon: "ic-times-tables" },
  { id: "mathsworkout", name: "Maths", icon: "ic-maths" },
  { id: "homework", name: "Homework", icon: "ic-brain" }, // LB-364: one row per practice go
];

const byId = Object.fromEntries(HISTORY_GAMES.map((g) => [g.id, g]));
export const historyGame = (id) => byId[id] || { id, name: id, icon: "ic-brain" };

// Every game with a slot in the filter: the registry's, in order, then any others that
// appear in this child's history.
export function historyGameIds(history) {
  const known = HISTORY_GAMES.map((g) => g.id);
  const extra = Object.keys(history || {}).filter((k) => !known.includes(k) && Array.isArray(history[k]) && history[k].length);
  return [...known, ...extra];
}

// ---- writing --------------------------------------------------------------------------

// Appends a settled test and keeps the newest HISTORY_PER_GAME for that game. Returns a new
// object; the caller writes it back. `rec.id` and `rec.at` are made by the caller OUTSIDE
// the state updater, because Root replays updaters on a save conflict and a fresh id on the
// replay would orphan the reward stamp below.
export function recordTest(history, rec, limit = HISTORY_PER_GAME) {
  const h = history && typeof history === "object" ? { ...history } : {};
  if (!rec || !rec.game || !rec.id) return h;
  const list = Array.isArray(h[rec.game]) ? h[rec.game].filter((r) => r && r.id !== rec.id) : [];
  h[rec.game] = [...list, rec].slice(-Math.max(1, limit));
  return h;
}

// The token half of the reward is only known once App's onLearnEarn has run (it decides
// credit vs "sent to your parent" and applies a double day). It is stamped onto the record
// the same settlement just wrote.
// `requestId` (23 Sep): an over-cap reward becomes a parent request. The record keeps its id
// and liveRecord() reads that request's status whenever the history is drawn, so approving or
// declining it shows everywhere at once — the record never holds a copy that can go stale.
export function stampReward(history, game, id, { tokens = 0, sent = false, requestId = null } = {}) {
  const h = history && typeof history === "object" ? { ...history } : {};
  if (!Array.isArray(h[game])) return h;
  h[game] = h[game].map((r) => (r && r.id === id ? { ...r, tokens: Math.max(0, tokens || 0), sent: !!sent, ...(requestId ? { requestId } : {}) } : r));
  return h;
}

// A record as it stands NOW: a sent reward reads its request's live status.
//   pending (or not found) → still sent   approved → paid, the request's amount   rejected → declined
export function liveRecord(rec, requests) {
  if (!rec || !rec.sent || !rec.requestId) return rec;
  const req = (requests || []).find((q) => q && q.id === rec.requestId);
  if (!req || req.status === "pending") return rec;
  if (req.status === "approved") return { ...rec, sent: false, tokens: req.requestedAmount ?? rec.tokens, approved: true };
  return { ...rec, sent: false, tokens: 0, declined: true };
}

// Builds the record from what a game reports at settlement. `questions` rows are
// { q, given, answer, correct, skipped?, timedOut?, meaning? } — display text only.
export function buildRecord({ id, at, game, questions = [], total, stopped = false, source = "coach", oinks = 0, qualified = null, gateOpen = true, streak = null, passMark = null, passKind = "pct" }) {
  const qs = (questions || []).map((r) => ({
    q: String(r.q ?? ""),
    given: r.given == null ? "" : String(r.given),
    answer: r.answer == null ? "" : String(r.answer),
    correct: !!r.correct,
    ...(r.skipped ? { skipped: true } : {}),
    ...(r.timedOut ? { timedOut: true } : {}),
    ...(r.meaning ? { meaning: String(r.meaning) } : {}),
  }));
  const score = qs.filter((r) => r.correct).length;
  return {
    id, at, game,
    score,
    total: Number.isFinite(total) && total > 0 ? total : qs.length,
    answered: qs.length,
    stopped: !!stopped,
    source,
    oinks: Math.max(0, oinks || 0),
    tokens: 0,
    sent: false,
    qualified: stopped ? null : qualified,
    gateOpen: !!gateOpen,
    ...(streak != null ? { streak } : {}),
    // The pass mark AT THE TIME: a parent may change it later, and the detail must say what
    // this test was judged against. "pct" = percentage; "streak" = times-tables streak length.
    passMark: Number.isFinite(passMark) ? passMark : null,
    passKind,
    questions: qs,
  };
}

// ---- the settlement writer App.jsx uses -----------------------------------------------
// Every game settles by calling onItemResults and THEN — only if it pays — onLearnEarn,
// synchronously, in one effect. record() writes the test and remembers it; stamp() adds the
// tokens onLearnEarn credited (or sent to a parent) to THAT record and forgets it. A stopped
// test never pays, so it is not remembered. Kept here, not inline in App, so the pairing is
// testable without mounting the app (scripts/check-test-history.mjs).
//
// update(fn, opts) is Root's updater; fn may be replayed after a save conflict, so the id and
// timestamp are made before it is called, never inside it.
export function makeHistoryWriter({ update, coachOn, makeId, now }) {
  let pending = null;
  return {
    record(childId, game, opts, abandoned) {
      const h = opts && opts.history;
      if (!h || !childId || !game) { pending = null; return null; }
      const on = typeof coachOn === "function" ? coachOn() : !!coachOn;
      const rec = buildRecord({
        id: makeId(), at: now(), game,
        questions: h.questions, total: opts.total, stopped: !!abandoned,
        // Who chose the questions: Learning Coach, the parent's own list under Coach ("Your
        // list"), or a household without Coach, where every test is the parent's.
        // A homework go is neither: the parent set the list, but it is practice, not a test
        // (LB-364), so it carries no "Your list" tag and no pass wording.
        source: game === "homework" ? "homework" : !on ? "manual" : opts.selection === "manual" ? "list" : "coach",
        oinks: h.oinks, qualified: h.qualified, gateOpen: h.gateOpen,
        passMark: h.passMark, passKind: h.passKind, streak: h.streak ?? null,
      });
      pending = abandoned ? null : { childId, game, id: rec.id };
      update((s) => {
        if (!s.learningContent) s.learningContent = {};
        if (!s.learningContent[childId]) s.learningContent[childId] = { spellingWords: [], vocabBank: [] };
        const lc = s.learningContent[childId];
        lc.testHistory = recordTest(lc.testHistory, rec);
        return s;
      }, { auto: true });
      return rec;
    },
    stamp(childId, game, amount, sent, requestId = null) {
      const ref = pending;
      pending = null;
      if (!ref || ref.game !== game || ref.childId !== childId) return false;
      update((s) => {
        const lc = s.learningContent && s.learningContent[ref.childId];
        if (!lc || !lc.testHistory) return null;
        lc.testHistory = stampReward(lc.testHistory, ref.game, ref.id, { tokens: amount, sent, requestId });
        return s;
      }, { auto: true });
      return true;
    },
  };
}

// ---- reading --------------------------------------------------------------------------

// Newest first. `game` null = every game, each still capped at the per-game limit (so All
// shows up to limit × games, never an overall cap).
export function historyList(history, game = null, limit = HISTORY_PER_GAME) {
  const h = history || {};
  const ids = game ? [game] : historyGameIds(h);
  const rows = ids.flatMap((g) => (Array.isArray(h[g]) ? h[g].slice(-limit) : []));
  return rows.sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
}

export function findTest(history, game, id) {
  const list = (history || {})[game];
  return Array.isArray(list) ? list.find((r) => r && r.id === id) || null : null;
}

// What a record says about pay, as one of a closed set the screens word for their reader.
//   stopped — left before the last question; never pays
//   sent    — over the weekly spending cap, waiting for a parent
//   declined — sent to a parent, who said no (read through liveRecord)
//   paid    — tokens credited
//   used    — scored enough, but this game's payouts for the week were already used
//   below   — did not reach the pass mark
export function payState(rec) {
  if (!rec) return "below";
  if (rec.stopped) return "stopped";
  if (rec.declined) return "declined";
  if (rec.sent) return "sent";
  if ((rec.tokens || 0) > 0) return "paid";
  if (rec.qualified && !rec.gateOpen) return "used";
  return "below";
}

// "Today, 16:12" · "Yesterday, 09:05" · "Mon 21 Sep, 18:05" · "Mon 21 Sep 25, 18:05".
// 24-hour, the app's own stamp convention (fmtActivityStamp).
export function historyStamp(iso, now = new Date()) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const dayStart = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((dayStart(now) - dayStart(d)) / 86400000);
  if (diff === 0) return `Today, ${hm}`;
  if (diff === 1) return `Yesterday, ${hm}`;
  const day = d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }).replace(",", "");
  const yr = d.getFullYear() === now.getFullYear() ? "" : ` ${String(d.getFullYear() % 100).padStart(2, "0")}`;
  return `${day}${yr}, ${hm}`;
}

// A maths prompt reads as a sum ("7 × 8") or as a question ("What comes after 19?").
export function sumLine(q, answer) {
  return String(q).includes("?") ? null : `${q} = ${answer}`;
}
